import React, { useState } from 'react';
import { Plus, Edit2, Trash2, ExternalLink, Search, X, Loader2, Link2 } from 'lucide-react';
import { useStore, put, remove, generateId } from '../hooks/useDB';
import { confirm } from '../utils/confirm';
import { toast } from '../utils/toast';

const PRESET_CATEGORIES = ['Customers', 'Engineering', 'Design','HR & Admin', 'Tools', 'Documentation', 'Finance', 'Other'];

const CATEGORY_COLORS = {
  'Customers':    { bg: 'rgba(124,58,237,0.1)',  text: '#6d28d9',  border: 'rgba(124,58,237,0.2)' },
  'Engineering':   { bg: 'rgba(59,130,246,0.1)',  text: '#1d4ed8',  border: 'rgba(59,130,246,0.2)' },
  'Design':        { bg: 'rgba(236,72,153,0.1)',  text: '#be185d',  border: 'rgba(236,72,153,0.2)' },
  'HR & Admin':    { bg: 'rgba(16,185,129,0.1)',  text: '#065f46',  border: 'rgba(16,185,129,0.2)' },
  'Tools':         { bg: 'rgba(249,101,22,0.1)',  text: '#c2410c',  border: 'rgba(249,101,22,0.2)' },
  'Documentation': { bg: 'rgba(245,158,11,0.1)',  text: '#92400e',  border: 'rgba(245,158,11,0.2)' },
  'Finance':       { bg: 'rgba(20,184,166,0.1)',  text: '#0f766e',  border: 'rgba(20,184,166,0.2)' },
  'Other':         { bg: 'rgba(107,114,128,0.1)', text: '#374151',  border: 'rgba(107,114,128,0.2)' },
};

function categoryStyle(cat) {
  return CATEGORY_COLORS[cat] || { bg: 'rgba(107,114,128,0.1)', text: '#374151', border: 'rgba(107,114,128,0.2)' };
}

// Links created before the "Product" category was renamed still have the old value saved —
// normalize on read so they display as "Customers" until next edit re-saves them.
const LEGACY_CATEGORY_ALIASES = { Product: 'Customers' };
function normalizeCategory(cat) {
  return LEGACY_CATEGORY_ALIASES[cat] || cat;
}

function categoryRank(cat) {
  const i = PRESET_CATEGORIES.indexOf(cat);
  return i === -1 ? PRESET_CATEGORIES.length : i;
}

function getDomain(url) {
  try { return new URL(url).hostname.replace('www.', ''); } catch { return url; }
}

const EMPTY_FORM = { title: '', description: '', url: '', category: 'Engineering', emoji: '🔗' };

export default function LinksViewer({ currentUser, canManage, canDelete }) {
  const isAdmin = currentUser?.role === 'admin';
  // Granted per-email by the caller; fall back to the admin role when unspecified
  const allowManage = canManage ?? isAdmin;
  const allowDelete = canDelete ?? isAdmin;
  const { data: links, loading } = useStore('links');

  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [modal, setModal] = useState(null); // null | 'add' | { ...link }
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingIds, setDeletingIds] = useState(new Set());

  const categories = [
    'All',
    ...Array.from(new Set(links.map(l => normalizeCategory(l.category)).filter(Boolean)))
      .sort((a, b) => categoryRank(a) - categoryRank(b)),
  ];

  const filtered = links
    .map(link => ({ ...link, category: normalizeCategory(link.category) }))
    .filter(link => {
      const matchCat = activeCategory === 'All' || link.category === activeCategory;
      const q = search.toLowerCase();
      const matchSearch = !q || link.title?.toLowerCase().includes(q) || link.description?.toLowerCase().includes(q) || link.url?.toLowerCase().includes(q);
      return matchCat && matchSearch;
    });

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setModal('add');
  };

  const openEdit = (link) => {
    setForm({ title: link.title || '', description: link.description || '', url: link.url || '', category: link.category || 'Engineering', emoji: link.emoji || '🔗' });
    setModal(link);
  };

  const closeModal = () => { setModal(null); setForm(EMPTY_FORM); };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.url.trim()) { toast.error('URL is required'); return; }
    let url = form.url.trim();
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) url = 'https://' + url;

    setSaving(true);
    try {
      const id = modal === 'add' ? `link-${generateId()}` : modal.id;
      await put('links', {
        ...(modal !== 'add' ? modal : {}),
        id,
        title: form.title.trim(),
        description: form.description.trim(),
        url,
        category: form.category,
        emoji: form.emoji || '🔗',
        createdByEmail: currentUser?.email,
      });
      toast.success(modal === 'add' ? 'Link added' : 'Link updated');
      closeModal();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (link) => {
    const ok = await confirm(`Remove "${link.title}"? This cannot be undone.`, 'Delete Link');
    if (!ok) return;
    setDeletingIds(s => new Set(s).add(link.id));
    try {
      await remove('links', link.id);
      toast.success('Link deleted');
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    } finally {
      setDeletingIds(s => { const n = new Set(s); n.delete(link.id); return n; });
    }
  };

  return (
    <div style={{ padding: '28px 32px' }}>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: '0 1 420px', minWidth: 180 }}>
          <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search links…"
            style={{ paddingLeft: 36, width: '100%', boxSizing: 'border-box' }}
          />
        </div>

        {allowManage && (
          <button className="btn btn-primary" onClick={openAdd} style={{ gap: 8, flexShrink: 0, marginLeft: 'auto' }}>
            <Plus size={16} /> Add Link
          </button>
        )}
      </div>

      {/* Category filter pills */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 28 }}>
        {categories.map(cat => {
          const isActive = cat === activeCategory;
          const cs = cat === 'All' ? null : categoryStyle(cat);
          return (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                padding: '5px 14px',
                borderRadius: 20,
                fontSize: 12, fontWeight: 600,
                border: isActive
                  ? (cat === 'All' ? '1.5px solid var(--accent)' : `1.5px solid ${cs.border}`)
                  : '1.5px solid var(--border-solid)',
                background: isActive
                  ? (cat === 'All' ? 'rgba(249,101,22,0.1)' : cs.bg)
                  : 'transparent',
                color: isActive
                  ? (cat === 'All' ? 'var(--accent)' : cs.text)
                  : 'var(--text2)',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {cat}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>
          <Loader2 size={24} style={{ animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState canManage={allowManage} hasLinks={links.length > 0} onAdd={openAdd} />
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 20,
        }}>
          {filtered.map(link => (
            <LinkCard
              key={link.id}
              link={link}
              canManage={allowManage}
              canDelete={allowDelete}
              deleting={deletingIds.has(link.id)}
              onEdit={openEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Add / Edit Modal */}
      {modal && (
        <LinkModal
          isEdit={modal !== 'add'}
          form={form}
          setForm={setForm}
          saving={saving}
          onSave={handleSave}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

function LinkCard({ link, canManage, canDelete, deleting, onEdit, onDelete }) {
  const [hovered, setHovered] = useState(false);
  const cs = categoryStyle(link.category);

  return (
    <div
      className="card"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', flexDirection: 'column',
        padding: '20px',
        transition: 'all 0.2s',
        transform: hovered ? 'translateY(-2px)' : 'none',
        boxShadow: hovered ? 'var(--shadow)' : undefined,
        position: 'relative',
        gap: 0,
      }}
    >
      {/* Top row: emoji + category */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{
          width: 42, height: 42,
          borderRadius: 12,
          background: cs.bg,
          border: `1px solid ${cs.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 20,
        }}>
          {link.emoji || '🔗'}
        </div>
        {link.category && (
          <span style={{
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
            color: cs.text, background: cs.bg, border: `1px solid ${cs.border}`,
            padding: '3px 8px', borderRadius: 20,
          }}>{link.category}</span>
        )}
      </div>

      {/* Title */}
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 6, lineHeight: 1.3 }}>
        {link.title}
      </div>

      {/* Description */}
      {link.description && (
        <div style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 14, flex: 1 }}>
          {link.description}
        </div>
      )}

      {/* Domain chip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text3)', fontSize: 11, marginBottom: 14 }}>
        <Link2 size={11} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getDomain(link.url)}</span>
      </div>

      {/* Actions row */}
      <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary"
          style={{ flex: 1, justifyContent: 'center', gap: 6, fontSize: 13, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
        >
          Open <ExternalLink size={13} />
        </a>
        {canManage && (
          <button className="btn btn-secondary btn-icon" onClick={() => onEdit(link)} title="Edit">
            <Edit2 size={14} />
          </button>
        )}
        {canDelete && (
          <button
            className="btn btn-secondary btn-icon"
            onClick={() => onDelete(link)}
            disabled={deleting}
            title="Delete"
            style={{ color: 'var(--danger)' }}
          >
            {deleting ? <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Trash2 size={14} />}
          </button>
        )}
      </div>
    </div>
  );
}

function EmptyState({ canManage, hasLinks, onAdd }) {
  return (
    <div style={{ textAlign: 'center', padding: '64px 24px', color: 'var(--text3)' }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>🔗</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text2)', marginBottom: 8 }}>
        {hasLinks ? 'No links match your filter' : 'No links yet'}
      </div>
      <div style={{ fontSize: 13, marginBottom: 20 }}>
        {hasLinks
          ? 'Try clearing the search or switching categories.'
          : canManage
            ? 'Add your first link to get started.'
            : 'Links added by admins will appear here.'}
      </div>
      {canManage && !hasLinks && (
        <button className="btn btn-primary" onClick={onAdd}>
          <Plus size={15} /> Add First Link
        </button>
      )}
    </div>
  );
}

const EMOJI_SUGGESTIONS = ['🔗', '📄', '🎨', '⚙️', '📊', '🛠️', '📋', '💡', '🚀', '📁', '🔑', '🌐'];

function LinkModal({ isEdit, form, setForm, saving, onSave, onClose }) {
  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));

  return (
    <>
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(4px)', zIndex: 400 }}
        onClick={onClose}
      />
      <div
        style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          background: 'var(--surface-solid)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 480,
          zIndex: 401, boxShadow: '0 24px 64px rgba(0,0,0,0.15)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text)' }}>
            {isEdit ? 'Edit Link' : 'Add New Link'}
          </div>
          <button className="btn btn-icon btn-secondary" onClick={onClose}><X size={16} /></button>
        </div>

        {/* Emoji picker */}
        <div className="form-group" style={{ marginBottom: 16 }}>
          <label>Icon</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {EMOJI_SUGGESTIONS.map(em => (
              <button
                key={em}
                onClick={() => f('emoji', em)}
                style={{
                  width: 36, height: 36, borderRadius: 10, fontSize: 18,
                  border: form.emoji === em ? '2px solid var(--accent)' : '1.5px solid var(--border-solid)',
                  background: form.emoji === em ? 'rgba(249,101,22,0.08)' : 'transparent',
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                {em}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-group">
            <label>Title *</label>
            <input value={form.title} onChange={e => f('title', e.target.value)} placeholder="e.g. Figma Design System" />
          </div>

          <div className="form-group">
            <label>URL *</label>
            <input value={form.url} onChange={e => f('url', e.target.value)} placeholder="https://..." />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={form.description}
              onChange={e => f('description', e.target.value)}
              placeholder="Brief description of what this link is about…"
              rows={2}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="form-group">
            <label>Category</label>
            <select value={form.category} onChange={e => f('category', e.target.value)}>
              {PRESET_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button className="btn btn-primary" onClick={onSave} disabled={saving} style={{ flex: 1, justifyContent: 'center' }}>
            {saving ? <><Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> Saving…</> : (isEdit ? 'Save Changes' : 'Add Link')}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}
